/**
 * Sidebar bottom rows: notifications bell + dark/light mode toggle
 */

import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { NotificationBell } from '../../features/notifications/components/NotificationBell';

interface SidebarQuickSettingsProps {
  isCollapsed: boolean;
  isMobileDrawer: boolean;
  isAnonymous: boolean;
}

export const SidebarQuickSettings: React.FC<SidebarQuickSettingsProps> = ({
  isCollapsed,
  isMobileDrawer,
  isAnonymous,
}) => {
  const { t } = useTranslation();
  const { mode, toggleTheme } = useTheme();
  const collapsed = isCollapsed && !isMobileDrawer;
  // Mobile already shows the bell in MobileHeader
  const showBell = !isAnonymous && !isMobileDrawer;
  const rowClassName = `flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`;
  const labelClassName = 'font-medium text-gray-700 dark:text-slate-300';

  return (
    <Box className="py-2">
      {showBell && (
        <Box className={rowClassName}>
          {!collapsed && (
            <Typography variant="body2" className={labelClassName}>
              {t('notifications.title')}
            </Typography>
          )}
          <NotificationBell popoverPlacement="right-end" />
        </Box>
      )}

      <Box className={rowClassName}>
        {!collapsed && (
          <Typography variant="body2" className={labelClassName}>
            {mode === 'light' ? t('theme.lightMode') : t('theme.darkMode')}
          </Typography>
        )}
        <Tooltip title={mode === 'light' ? t('theme.switchToDark') : t('theme.switchToLight')}>
          <IconButton
            onClick={toggleTheme}
            className="text-gray-600 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
            size="small"
            sx={{ minWidth: 44, minHeight: 44 }} // Touch target optimization
          >
            {mode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
